import _ from 'lodash';

/**
 * Drill-Down auf der Statistikseite: erst der LinkedIn-Account, dann dessen Kampagnen.
 *
 * Die Kampagnenliste, die die Seite ohnehin lädt, trägt den Account bereits als ID
 * (`normalizeFunnelResponse` in `API/linkedin-funnel.ts` schreibt `linkedinAccountId` nach
 * `linkedinAccount`). Der Filter braucht deshalb keinen zweiten Abruf — er engt die schon
 * vorhandene Liste ein, und das Umschalten kostet keine Wartezeit.
 */

/** Eine Kampagne, soweit dieser Filter sie kennen muss. */
export interface CampaignWithAccount {
  _id: string;
  linkedinAccount?: string | { _id?: string } | null;
}

/**
 * Das Kampagnen-Objekt trägt den Account je nach Endpunkt als ID oder als Unterobjekt.
 * Beide Formen kommen in dieser Oberfläche vor, deshalb liest der Filter beide.
 */
export function accountIdOf(campaign: CampaignWithAccount): string | undefined {
  const account = campaign.linkedinAccount;

  if (typeof account === 'string') {
    return account === '' ? undefined : account;
  }

  if (!_.isNil(account) && typeof account === 'object' && typeof account._id === 'string') {
    return account._id;
  }

  return undefined;
}

/** Ohne gewählten Account bleibt alles sichtbar — der Filter sperrt nicht, er engt ein. */
export function campaignsForAccounts<T extends CampaignWithAccount>(campaigns: T[], accountIds: string[]): T[] {
  if (_.isEmpty(accountIds)) {
    return campaigns;
  }

  return campaigns.filter((campaign) => {
    const accountId = accountIdOf(campaign);
    return !_.isNil(accountId) && accountIds.includes(accountId);
  });
}

/**
 * Welche Kampagnen nach einem Accountwechsel ausgewählt sein sollen.
 *
 * Eine von Hand getroffene Auswahl bleibt erhalten, soweit sie zum Account passt; fällt sie
 * ganz weg (anderer Account), tritt dessen vollständiges Bild an ihre Stelle. Ohne das
 * stünde nach jedem Wechsel eine leere Auswahl da, und „Statistiken laden" bliebe gesperrt.
 */
export function selectionForAccounts<T extends CampaignWithAccount>(
  currentSelection: string[],
  campaigns: T[],
  accountIds: string[]
): string[] {
  if (_.isEmpty(accountIds)) {
    return currentSelection;
  }

  const visible = campaignsForAccounts(campaigns, accountIds);
  const visibleIds = visible.map((campaign) => campaign._id);
  const kept = currentSelection.filter((id) => visibleIds.includes(id));

  return _.isEmpty(kept) ? visibleIds : kept;
}
